import { ReactNode, useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import getSorElements from 'common/use-cases/get-sor-elements'
import getSorMapping from 'common/use-cases/get-sor-mapping'
import Roles from 'config/Roles'
import RolesMap from 'config/RolesMap'
import { useAuth } from 'hooks'
import HttpClient from 'httpClient'
import { toast } from 'react-toastify'
import RepoFactory from 'repoFactory'
import { AppConfig, ElementMapping, SORList } from 'ts/types'
import { AppContext } from './AppContext'

const fetchAppConfig = async () => {
  const response = await fetch('/config.json', {
    headers: { 'Cache-Control': 'no-cache' }
  })
  return await response.json()
}

const axiosClient = {
  all: axios.all,
  request: axios.request,
  retry: axios,
  interceptors: axios.interceptors
}

const httpClientConfig = { timeout: 10000 }

const mapRolesFromConfig = (config: AppConfig) => {
  if (!config) return

  RolesMap[Roles.ADMINS] = config.systemAdmins || RolesMap[Roles.ADMINS]
  RolesMap[Roles.FINANCIAL_ADMINS] = config.financialAdmins || RolesMap[Roles.FINANCIAL_ADMINS]
  RolesMap[Roles.BU_MANAGERS] = config.buManagers || RolesMap[Roles.BU_MANAGERS]
  RolesMap[Roles.DEPARTMENT_MANAGERS] = config.departmentManagers || RolesMap[Roles.DEPARTMENT_MANAGERS]
  RolesMap[Roles.PORTFOLIO_MANAGERS] = config.portfolioManagers || RolesMap[Roles.PORTFOLIO_MANAGERS]
  RolesMap[Roles.PRODUCT_MANAGERS] = config.productManagers || RolesMap[Roles.PRODUCT_MANAGERS]
}

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const { isLoggedIn, user } = useAuth()
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [repoFactory, setRepoFactory] = useState<RepoFactory | null>(null)
  const [isQEnabled, setIsQEnabled] = useState(false)
  const [error, setError] = useState(false)
  const [isAppReady, setIsAppReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // SOR states
  const currentYear = new Date().getFullYear()
  const [sorElements, setSorElements] = useState<SORList | null>(null)
  const [sorElementMapping, setSorElementMapping] = useState<ElementMapping | null>(null)
  const [lastRunTime, setLastRunTime] = useState(null)
  const [availableYears, setAvailableYears] = useState([currentYear - 1, currentYear])
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [sorDataLoaded, setSorDataLoaded] = useState(false)

  useEffect(() => {
    fetchAppConfig()
      .then((configData) => {
        setConfig(configData)
        setError(false)
      })
      .catch((err) => {
        setError(true)
        console.error('Failed to load configuration:', err)
      })
  }, [])

  useEffect(() => {
    if (!config) return

    mapRolesFromConfig(config)

    setIsQEnabled(config.isQEnabled === 'true' || config.isQEnabled === true)

    const httpClientInstance = new HttpClient({
      axiosClient,
      config: {
        ...httpClientConfig,
        ...(user?.idToken && {
          headers: { Authorization: `Bearer ${user.idToken}` }
        })
      }
    })

    const repoFactoryInstance = new RepoFactory({
      httpClient: httpClientInstance,
      GRAPHQL_URL: config.REACT_APP_GRAPHQL_URL,
      GRAPHQL_API_KEY: config.REACT_APP_GRAPHQL_API_KEY,
      ID_TOKEN: user?.idToken
    })

    setRepoFactory(repoFactoryInstance)
  }, [config, user])

  const fetchSorMapping = useCallback(
    (year: number) => {
      if (!repoFactory) return
      getSorMapping(year, {
        commonRepo: repoFactory.commonRepo(),
        observer: {
          success: ({
            availableYears,
            currentYearMapping
          }: {
            availableYears: number[]
            currentYearMapping: ElementMapping
          }) => {
            setAvailableYears(availableYears)
            setSelectedYear(year)
            setSorElementMapping(currentYearMapping)
            setSorDataLoaded(true)
          },
          failure: () => {
            console.error('Error fetching SOR mapping')
            setSorDataLoaded(true)
          }
        }
      })
    },
    [repoFactory]
  )

  useEffect(() => {
    if (repoFactory && isLoggedIn()) {
      fetchSorMapping(currentYear)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoFactory, currentYear, fetchSorMapping])

  useEffect(() => {
    if (repoFactory && isLoggedIn() && selectedYear !== currentYear) {
      fetchSorMapping(selectedYear)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, fetchSorMapping, currentYear])

  useEffect(() => {
    if (!repoFactory || !isLoggedIn()) return

    getSorElements(selectedYear, {
      commonRepo: repoFactory.commonRepo(),
      observer: {
        elementsReceived: (elements: SORList) => {
          setSorElements(elements)
          setSorDataLoaded(true)
        },
        errorGettingData: () => {
          toast.error('Error getting SOR elements')
          setSorElements(null)
          setSorDataLoaded(true)
        },
        receiveLastRunTime: setLastRunTime
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoFactory, selectedYear])

  useEffect(() => {
    const isReadyForLoggedOut = !isLoggedIn() && repoFactory
    const isReadyForLoggedIn = isLoggedIn() && config && repoFactory && sorDataLoaded

    if (isReadyForLoggedOut || isReadyForLoggedIn) {
      setIsAppReady(true)
    }
  }, [config, repoFactory, sorDataLoaded, isLoggedIn])

  return (
    <AppContext.Provider
      value={{
        repoFactory,
        isQEnabled,
        error,
        lastRunTime,
        sorElements,
        sorElementMapping,
        currentYear,
        availableYears,
        selectedYear,
        setSelectedYear,
        version: config?.version || '',
        packagingType: config?.packagingType || '',
        accountMappingLimit: Number(config?.accountMappingLimit) || 25,
        isAppReady,
        setIsLoading,
        isLoading
      }}
    >
      {children}
    </AppContext.Provider>
  )
}
